import { onBeforeRouteLeave } from 'vue-router'
import { debounce } from 'lodash-es'
import {
  computed,
  reactive,
  unref,
  watch,
  ref,
  watchEffect,
  toRefs,
  useResizeObserver,
} from '@/composition/vue/compositionApi'
import type { MaybeRef, Ref } from '@/composition/vue/compositionApi'
import type TmScrollbar from '@/components/shared/TmScrollbar.vue'
import { scrollEvents } from '@/constants/events'
import { TmComponentCompositionError } from '@/core/error/composition/tmComponentCompositionError'
import type { Callback } from '@/types'

type ScrollInfo = { scrollTop: number }
type UseScrollProgressResult = {
  nowScrolling: boolean
  scrollStartedAt: number
  scrollDelta: number
  scrollDirection: 'up' | 'down'
}
type OnDirectedScrollCallback = Callback<void, [UseScrollProgressResult]>
type OnScrollCallback = Callback<void, [Event, ScrollInfo]>
type OnScrollbarInitCallback = Callback<void, [InstanceType<typeof TmScrollbar>]>
type OnTriggerZoneScrollCallback = Callback<void, [HTMLDivElement]>

export type ScrollbarRef = Ref<InstanceType<typeof TmScrollbar> | undefined>

export const scrollBarSymbol = Symbol('scrollBar')

const addCallback = (to: Set<unknown>, cb: Callback<any>) => {
  to.add(cb)
  return () => to.delete(cb)
}

export const useScrollbarHelpers = (scrollbarRef: ScrollbarRef) => {
  const onScrollCallbacks = new Set<OnScrollCallback>()
  const onScrollbarInitCallbacks = new Set<OnScrollbarInitCallback>()
  const { scrollSmoothly } = useCustomSmoothScroll(computed(() => getScrollbar().scroll!))
  const scrollInfo = reactive<ScrollInfo>({
    scrollTop: 0,
  })

  const handleScroll = (evt: Event) => {
    onScrollCallbacks.forEach((cb) => cb(evt, scrollInfo))
  }

  watch(
    scrollbarRef,
    (newScrollbar, oldScrollbar) => {
      if (newScrollbar) {
        onScrollbarInitCallbacks.forEach((cb) => cb(newScrollbar))
        scrollEvents.forEach((event) => {
          newScrollbar.$el.addEventListener(event, handleScroll, {
            passive: true,
          })
        })
      }
      if (oldScrollbar) {
        scrollEvents.forEach((event) => {
          oldScrollbar.$el.removeEventListener(event, handleScroll)
        })
      }
    },
    { immediate: true },
  )

  const hasScrollbar = () => !!unref(scrollbarRef)
  const getScrollbar = () => {
    const value = unref(scrollbarRef)
    if (!value) {
      throw new TmComponentCompositionError('Scrollbar ref is empty')
    }
    return value
  }
  const getScrollElement = (): HTMLDivElement => {
    const elem = getScrollbar().scroll
    if (!elem) {
      throw new TmComponentCompositionError('Scrollbar element not found')
    }
    return elem
  }

  const onScrollbarInit = (cb: OnScrollbarInitCallback) => addCallback(onScrollbarInitCallbacks, cb)
  const onScroll = (cb: OnScrollCallback) => addCallback(onScrollCallbacks, cb)

  const scrollToEnd = async (behavior: ScrollBehavior = 'instant') => {
    if (behavior === 'smooth') {
      await scrollSmoothly(getScrollHeight(), 300)
      return
    }

    getScrollElement().scrollTo({ top: getScrollHeight() })
  }
  const scrollToTop = (behavior: ScrollBehavior = 'instant') => {
    if (behavior === 'smooth') {
      scrollSmoothly(0, 300)
      return
    }

    getScrollElement().scrollTo({ top: 0 })
  }

  onScroll(() => {
    scrollInfo.scrollTop = getScrollElement().scrollTop
  })

  const getScrollHeight = () => getScrollElement().scrollHeight
  const getScrollTop = () => getScrollElement().scrollTop

  onBeforeRouteLeave(() => {
    onScrollCallbacks.clear()
    onScrollbarInitCallbacks.clear()
  })

  const getScrollOffsetHeight = () => getScrollElement().offsetHeight

  const getRealScrollHeight = () => {
    return getScrollHeight() - getScrollOffsetHeight()
  }

  return {
    getScrollbar,
    scrollToEnd,
    scrollToTop,
    getScrollElement,
    getScrollHeight,
    getScrollTop,
    scrollSmoothly,
    getRealScrollHeight,
    getScrollOffsetHeight,
    hasScrollbar,
    scrollInfo,

    onScrollbarInit,
    onScroll,
  }
}

export const useScrollbarTopTriggerZone = (scrollbarRef: ScrollbarRef, triggerScrollHeight: MaybeRef<number>) => {
  const { getScrollElement, scrollInfo } = useScrollbarHelpers(scrollbarRef)
  const { onScrollUp } = useScrollProgress(scrollbarRef)
  const onTriggerZoneScrollCallbacks = new Set<OnTriggerZoneScrollCallback>()

  onScrollUp(() => {
    if (scrollInfo.scrollTop < unref(triggerScrollHeight!)) {
      onTriggerZoneScrollCallbacks.forEach((cb) => cb(getScrollElement()))
    }
  })

  const onScrollToTriggerZone = (cb: OnTriggerZoneScrollCallback) => {
    onTriggerZoneScrollCallbacks.add(cb)
    return () => onTriggerZoneScrollCallbacks.delete(cb)
  }

  return {
    onScrollToTriggerZone,
  }
}

export const useScrollProgress = (scrollbarRef: ScrollbarRef, scrollStopTime = 400) => {
  const { onScroll, scrollInfo } = useScrollbarHelpers(scrollbarRef)
  const scrollStopTimeout = ref<NodeJS.Timeout | null>(null)
  const previousDelta = ref(0)
  const onScrollUpCallbacks = new Set<OnDirectedScrollCallback>()
  const onScrollDownCallbacks = new Set<OnDirectedScrollCallback>()

  const result = reactive<UseScrollProgressResult>({
    nowScrolling: false,
    scrollStartedAt: 0,
    scrollDelta: 0,
    scrollDirection: 'up',
  })

  const handleStopScrolling = () => {
    result.nowScrolling = false
    result.scrollStartedAt = scrollInfo.scrollTop
    result.scrollDelta = 0
    previousDelta.value = 0
    clearTimeout(scrollStopTimeout.value!)
    scrollStopTimeout.value = null
  }

  onScroll(() => {
    if (scrollStopTimeout.value !== null) {
      clearTimeout(scrollStopTimeout.value)
    }

    result.nowScrolling = true
    previousDelta.value = result.scrollDelta
    result.scrollDelta = scrollInfo.scrollTop - result.scrollStartedAt

    scrollStopTimeout.value = setTimeout(() => {
      handleStopScrolling()
    }, scrollStopTime)
  })

  watchEffect(
    () => {
      if (result.scrollDelta === previousDelta.value) {
        return
      }

      const previousDirection = result.scrollDirection
      result.scrollDirection = previousDelta.value > result.scrollDelta ? 'up' : 'down'

      if (previousDirection !== result.scrollDirection) {
        result.scrollStartedAt = scrollInfo.scrollTop
        result.scrollDelta = 0
      }

      if (!result.nowScrolling) {
        return
      }

      const callbacksToExecute = result.scrollDirection === 'up' ? onScrollUpCallbacks : onScrollDownCallbacks
      callbacksToExecute.forEach((cb) => cb(result))
    },
    { flush: 'post' },
  )

  const onScrollUp = (cb: OnDirectedScrollCallback) => addCallback(onScrollUpCallbacks, cb)
  const onScrollDown = (cb: OnDirectedScrollCallback) => addCallback(onScrollDownCallbacks, cb)

  return {
    ...toRefs(result),
    onScrollUp,
    onScrollDown,
  }
}

export const useBottomToTopScroll = (scrollbarRef: ScrollbarRef, thresholdPx = 50) => {
  const {
    onScrollbarInit,
    scrollToEnd,
    hasScrollbar,
    getScrollbar,
    getScrollElement,
    getRealScrollHeight,
    getScrollTop,
    onScroll,
  } = useScrollbarHelpers(scrollbarRef)
  const scrollWrapperElement = computed(() => (hasScrollbar() ? getScrollElement() : null))
  const scrollContentElement = computed(() => (hasScrollbar() ? getScrollbar().getScrollContentElement() : null))
  const wasScrolledToBottom = ref(true)
  const scrollLocked = ref(false)

  const unlockScroll = debounce(() => {
    scrollLocked.value = false
  }, 500)

  const lockScroll = () => {
    scrollLocked.value = true
    unlockScroll.cancel()
    unlockScroll()
  }

  const calculateWasScrolledToBottom = () => {
    const delta = Math.abs(getRealScrollHeight() - getScrollTop())
    wasScrolledToBottom.value = delta < thresholdPx
  }

  const keepScrolledToBottom = () => {
    if (wasScrolledToBottom.value) {
      scrollToEnd()
    }
    lockScroll()
    calculateWasScrolledToBottom()
  }

  onScroll(() => {
    if (scrollLocked.value) {
      return
    }
    calculateWasScrolledToBottom()
  })

  const createScrollContentObserver = () => useResizeObserver(scrollContentElement, keepScrolledToBottom)
  const createScrollWrapperObserver = () => useResizeObserver(scrollWrapperElement, keepScrolledToBottom)

  const startObservation = (scrollbarInitHandler?: () => Promise<void>, observeWrapper = false) => {
    createScrollContentObserver()

    if (observeWrapper) {
      createScrollWrapperObserver()
    }

    if (scrollbarInitHandler) {
      onScrollbarInit(scrollbarInitHandler)
    } else {
      onScrollbarInit(async () => {
        scrollToEnd()
      })
    }
  }

  return {
    startObservation,
    createScrollContentObserver,
    createScrollWrapperObserver,
    wasScrolledToBottom,
  }
}

// https://gist.github.com/felipenmoura/650e7e1292c1e7638bcf6c9f9aeb9dd5
export const useCustomSmoothScroll = (elementRef: MaybeRef<HTMLElement>) => {
  const easeInOutQuad = (currentTime: number, startTime: number, scrollChange: number, duration: number) => {
    let localTime = currentTime
    localTime /= duration / 2
    if (localTime < 1) return (scrollChange / 2) * localTime * localTime + startTime
    localTime -= 1
    return (-scrollChange / 2) * (localTime * (localTime - 2) - 1) + startTime
  }

  const animateScroll = (
    element: HTMLElement,
    startTime: number,
    scrollStart: number,
    scrollChange: number,
    duration: number,
    scrollTo: number,
    resolve: Callback<void>,
  ) => {
    const actionTime = Date.now()
    const currentTime = actionTime - startTime
    element.scrollTop = Math.round(easeInOutQuad(currentTime, scrollStart, scrollChange, duration))
    if (currentTime < duration) {
      requestAnimationFrame(() =>
        animateScroll(element, startTime, scrollStart, scrollChange, duration, scrollTo, resolve),
      )
    } else {
      element.scrollTop = scrollTo
      resolve()
    }
  }

  const scrollSmoothly = (to: number, duration: number) => {
    const { promise, resolve } = Promise.withResolvers<void>()
    const element = unref(elementRef)

    const scrollStart = element.scrollTop
    const scrollChange = to - scrollStart
    const startTime = Date.now()

    animateScroll(element, startTime, scrollStart, scrollChange, duration, to, resolve)
    return promise
  }

  return {
    scrollSmoothly,
  }
}
